import React, { useState } from "react";
import { Send, CheckCircle2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Community-tip form. Anyone can submit a convicted abuser they've found in the
// news or public records but isn't yet in our registry. Tips land in the admin
// queue for the same review + Claude-extractor pass the RSS cron uses.
export default function SubmitRegistryTip() {
  const [form, setForm] = useState({
    abuser_name: "",
    city: "",
    state: "",
    crime_description: "",
    source_url: "",
    tipster_email: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/registry-tips`, form);
      setSubmitted(true);
      toast.success(`Tip received — ID ${data.tip_id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="border border-emerald-600 bg-emerald-50 p-6"
        data-testid="tip-submitted-card"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="text-emerald-700" size={22} />
          <div>
            <div className="font-display font-black text-xl tracking-tight">
              Thanks — we've got it.
            </div>
            <p className="text-sm text-emerald-900 mt-2 leading-relaxed">
              A human reviewer will cross-check your source and, if the
              conviction is confirmed, add the record to the public registry.
              Tips that turn out to be unverifiable are quietly discarded.
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setForm({
                  abuser_name: "",
                  city: "",
                  state: "",
                  crime_description: "",
                  source_url: "",
                  tipster_email: "",
                });
              }}
              className="mt-4 text-xs uppercase tracking-widest font-bold underline"
              data-testid="submit-another-tip-btn"
            >
              Submit another tip
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="border border-zinc-900 bg-white p-6 space-y-4"
      data-testid="registry-tip-form"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={18} />
        <div className="text-xs text-zinc-600 leading-relaxed">
          Only submit <strong>convicted</strong> (or pleaded-guilty) animal
          abusers with a verifying news, court, or government source link. Tips
          without a source URL are automatically rejected.
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <LField label="Abuser name *" required>
          <input
            required
            value={form.abuser_name}
            onChange={update("abuser_name")}
            className="field"
            data-testid="tip-name-input"
            placeholder="Jane Doe"
          />
        </LField>
        <LField label="State">
          <input
            value={form.state}
            onChange={update("state")}
            className="field"
            data-testid="tip-state-input"
            placeholder="CA"
          />
        </LField>
        <LField label="City">
          <input
            value={form.city}
            onChange={update("city")}
            className="field"
            data-testid="tip-city-input"
            placeholder="Los Angeles"
          />
        </LField>
        <LField label="Your email (optional)">
          <input
            type="email"
            value={form.tipster_email}
            onChange={update("tipster_email")}
            className="field"
            data-testid="tip-email-input"
            placeholder="you@example.com"
          />
        </LField>
      </div>

      <LField label="Conviction / crime description *" required>
        <textarea
          required
          minLength={15}
          rows={3}
          value={form.crime_description}
          onChange={update("crime_description")}
          className="field"
          data-testid="tip-description-input"
          placeholder="Convicted of felony animal cruelty in 2024; sentenced to 2 years state prison for…"
        />
      </LField>

      <LField label="Source URL (news, court record, DOJ press release) *" required>
        <input
          required
          type="url"
          value={form.source_url}
          onChange={update("source_url")}
          className="field"
          data-testid="tip-source-input"
          placeholder="https://…"
        />
      </LField>

      <button
        type="submit"
        disabled={busy}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 border border-zinc-900 bg-zinc-900 text-white text-[11px] uppercase tracking-widest font-bold hover:bg-red-600 hover:border-red-600 disabled:opacity-50 transition-colors"
        data-testid="submit-tip-btn"
      >
        <Send size={14} /> {busy ? "Sending…" : "Send tip to reviewers"}
      </button>
    </form>
  );
}

function LField({ label, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
